import React, { useEffect, useRef, useState } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import ThreeGlobe from 'three-globe';
import * as THREE from 'three';
import { useConnectionData, statusColors } from './ConnectionData';
import { countryCentroidData } from './CenteroidData';
import { useMyContext } from "../../../context/MyContext";

// Helper function to create airplane sprite texture
const createAirplaneTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  
  ctx.clearRect(0, 0, 128, 128);
  ctx.save();
  ctx.translate(64, 64);
  
  // Draw airplane
  ctx.fillStyle = '#FBC337';
  ctx.beginPath();
  ctx.ellipse(0, 0, 30, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  
  // Wings
  ctx.beginPath();
  ctx.moveTo(-20, 0);
  ctx.lineTo(-12, -20);
  ctx.lineTo(5, -20);
  ctx.lineTo(12, 0);
  ctx.closePath();
  ctx.fill();
  
  ctx.beginPath();
  ctx.moveTo(-20, 0);
  ctx.lineTo(-12, 20);
  ctx.lineTo(5, 20);
  ctx.lineTo(12, 0);
  ctx.closePath();
  ctx.fill();
  
  // Tail
  ctx.beginPath();
  ctx.moveTo(-30, 0);
  ctx.lineTo(-35, -12);
  ctx.lineTo(-22, -10);
  ctx.lineTo(-22, 0);
  ctx.closePath();
  ctx.fill();
  
  // White outline for visibility
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(0, 0, 30, 8, 0, 0, Math.PI * 2);
  ctx.stroke();
  
  ctx.restore();
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
};

// Helper function to create flight sprites
const createFlightSprites = (globe, flightData) => {
  const texture = createAirplaneTexture();
  
  console.log('Creating flight sprites for', flightData.length, 'In Transit routes');
  
  flightData.forEach((flight, index) => {
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      sizeAttenuation: false, // Keep consistent size regardless of distance
      depthTest: false, // Don't occlude
      depthWrite: false,
      opacity: 1.0
    });
    
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(10, 10, 1); // Even larger for visibility
    sprite.renderOrder = 1000; // Render on top of everything
    sprite.name = `flight_${index}`;
    
    // Set initial position
    const pos = latLngToVector3(flight.startLat, flight.startLng, 100, 5);
    sprite.position.copy(pos);
    
    globe.add(sprite);
    flight.sprite = sprite;
    
    console.log(`Created sprite ${index} at position:`, sprite.position);
  });
  
  console.log('Globe children count:', globe.children.length);
};

// Helper to convert lat/lng to 3D coordinates
const latLngToVector3 = (lat, lng, radius, altitude = 0) => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  const r = radius + altitude;
  
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
};

const ImprovedGlobe = ({ setHoveredCountry, countries }) => {
  const { scene, camera, gl } = useThree();
  const { user } = useMyContext();
  const { connectionData, loading, error } = useConnectionData(user || {});
  
  const globeRef = useRef();
  const animationsRef = useRef([]);
  const hoveredCountryRef = useRef(null);
  const [globeReady, setGlobeReady] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);

  // Initialize globe
  useEffect(() => {
    if (!countries || countries.length === 0) return;

    // Create globe instance with custom water sphere
    const globe = new ThreeGlobe({
      waitForGlobeReady: true,
      animateIn: true,
    })
      .showGlobe(false); // We'll add custom water sphere
    
    // Add custom water sphere with previous color
    const waterMaterial = new THREE.MeshPhongMaterial({
      color: "#3888ff",  // Previous water color
      shininess: 30,
      transparent: true,
      opacity: 0.9
    });
    const sphereGeometry = new THREE.SphereGeometry(100, 64, 64);
    const waterSphere = new THREE.Mesh(sphereGeometry, waterMaterial);
    waterSphere.renderOrder = 0;
    globe.add(waterSphere);

    // Process countries data for polygons
    const polygonsData = countries
      .filter(d => d.properties && d.geometry)
      .map(d => ({
        ...d,
        properties: {
          ...d.properties,
          name: d.properties.name || d.properties.NAME || 'Unknown'
        }
      }));

    // Add polygons (countries) - removed polygonLabel as it's not available
    globe
      .polygonsData(polygonsData)
      .polygonCapColor(() => '#fa2832')
      .polygonSideColor(() => '#fa2832')
      .polygonStrokeColor(() => '#111')
      .polygonAltitude(0.01);

    // Scale and position globe
    globe.scale.set(0.02, 0.02, 0.02);
    globe.rotation.y = Math.PI;
    
    scene.add(globe);
    globeRef.current = globe;
    setGlobeReady(true);

    // Camera setup
    camera.position.set(0, 0, 350);
    camera.lookAt(0, 0, 0);

    return () => {
      if (globeRef.current) {
        scene.remove(globeRef.current);
        globeRef.current = null;
      }
    };
  }, [countries, scene, camera]);

  // Add connections and animations
  useEffect(() => {
    if (!globeReady || !globeRef.current || !connectionData.length) return;

    const globe = globeRef.current;

    // Clear previous animations
    animationsRef.current.forEach(anim => {
      if (anim.sprite) {
        globe.remove(anim.sprite);
      }
    });
    animationsRef.current = [];

    // Prepare arcs data from connections (static, no animation)
    const arcsData = connectionData
      .filter(conn => {
        const hasFrom = countryCentroidData[conn.fromCountry];
        const hasTo = countryCentroidData[conn.toCountry];
        if (!hasFrom || !hasTo) {
          console.warn(`Missing centroid for: ${conn.fromCountry} -> ${conn.toCountry}`);
        }
        return hasFrom && hasTo;
      })
      .map(conn => {
        const color = statusColors[conn.status] || statusColors['Collection'];
        const hexColor = `#${color.toString(16).padStart(6, '0')}`;
        return {
          startLat: countryCentroidData[conn.fromCountry].lat,
          startLng: countryCentroidData[conn.fromCountry].lon,
          endLat: countryCentroidData[conn.toCountry].lat,
          endLng: countryCentroidData[conn.toCountry].lon,
          color: hexColor,
          status: conn.status,
          fromCountry: conn.fromCountry,
          toCountry: conn.toCountry,
        };
      });

    // Add static arcs to globe (no dash animation)
    globe
      .arcsData(arcsData)
      .arcColor('color')
      .arcStroke(0.3)
      .arcAltitude(0.3)
      .arcAltitudeAutoScale(0.5)
      .arcDashLength(1) // Solid line
      .arcDashGap(0) // No gaps
      .arcDashAnimateTime(0); // No animation

    // Add labels for countries with shipments
    const labelsData = [];
    const uniqueCountries = new Set();
    
    connectionData.forEach(conn => {
      if (conn.fromCountry) uniqueCountries.add(conn.fromCountry);
      if (conn.toCountry) uniqueCountries.add(conn.toCountry);
    });

    uniqueCountries.forEach(countryName => {
      if (countryCentroidData[countryName]) {
        labelsData.push({
          lat: countryCentroidData[countryName].lat,
          lng: countryCentroidData[countryName].lon,
          text: countryName,
          color: '#ffffff',
          size: 0.6,
        });
      }
    });

    globe
      .labelsData(labelsData)
      .labelText('text')
      .labelSize('size')
      .labelDotRadius(0.4) // Larger dots
      .labelColor('color')
      .labelResolution(2)
      .labelAltitude(0.01);

    // Add points at endpoints for better visibility
    const pointsData = [];
    connectionData.forEach(conn => {
      if (countryCentroidData[conn.fromCountry]) {
        pointsData.push({
          lat: countryCentroidData[conn.fromCountry].lat,
          lng: countryCentroidData[conn.fromCountry].lon,
          size: 0.5,
          color: '#ffdd00',
        });
      }
      if (countryCentroidData[conn.toCountry]) {
        pointsData.push({
          lat: countryCentroidData[conn.toCountry].lat,
          lng: countryCentroidData[conn.toCountry].lon,
          size: 0.5,
          color: '#ffdd00',
        });
      }
    });

    globe
      .pointsData(pointsData)
      .pointColor('color')
      .pointAltitude(0.01)
      .pointRadius('size');

  }, [globeReady, connectionData]);

  // Handle mouse interactions
  useEffect(() => {
    if (!gl || !globeRef.current) return;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleMouseMove = (event) => {
      const rect = gl.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      
      // Check for intersections with globe
      const intersects = raycaster.intersectObject(globeRef.current, true);
      
      if (intersects.length > 0) {
        const intersect = intersects[0];
        
        // Try to find country from userData or object name
        let countryName = null;
        
        // Check userData for properties
        if (intersect.object.userData?.properties?.name) {
          countryName = intersect.object.userData.properties.name;
        }
        // Check parent objects
        else if (intersect.object.parent?.userData?.properties?.name) {
          countryName = intersect.object.parent.userData.properties.name;
        }
        
        if (countryName && countryName !== hoveredCountryRef.current) {
          hoveredCountryRef.current = countryName;
          if (setHoveredCountry) {
            setHoveredCountry(countryName);
          }
          setTooltipData({
            name: countryName,
            x: event.clientX,
            y: event.clientY
          });
        }
      } else {
        if (hoveredCountryRef.current) {
          hoveredCountryRef.current = null;
          if (setHoveredCountry) {
            setHoveredCountry(null);
          }
          setTooltipData(null);
        }
      }
    };

    gl.domElement.addEventListener('mousemove', handleMouseMove);

    return () => {
      gl.domElement.removeEventListener('mousemove', handleMouseMove);
    };
  }, [gl, camera, setHoveredCountry]);

  // Animation loop
  useFrame((state, delta) => {
    if (!globeRef.current) return;

    // Rotate globe slowly
    globeRef.current.rotation.y += 0.001;

    // Enforce zoom limits
    const minDistance = 150;
    const maxDistance = 400;
    const distance = camera.position.length();
    
    if (distance < minDistance) {
      camera.position.setLength(minDistance);
    } else if (distance > maxDistance) {
      camera.position.setLength(maxDistance);
    }
  });

  if (loading) return <group />;
  if (error) {
    console.warn('Globe data loading failed:', error);
    return <group />;
  }

  return null;
};

export default ImprovedGlobe;
